import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, IconButton } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';

const DEFAULT_BLOG_POSTS = [
  {
    id: 1,
    title: '¿CUÁL ES EL COSTE DE MANTENIMIENTO DE UN ASCENSOR?',
    publishDate: '2024-05-09',
    summary: 'En edificios, casas, locales comerciales, oficinas o centros comerciales, la revisión periódica de un ascensor debe ser una obligación...',
    imageUrl: 'https://images.unsplash.com/photo-1541888946425-d0fbb18615f3?w=300&q=80',
  },
  {
    id: 2,
    title: 'PRECIO DE INSTALAR UN ASCENSOR DE 4 PLANTAS. ¿Y UNO DE 3 PLANTAS?',
    publishDate: '2024-05-06',
    summary: 'Actualmente, el precio de instalar un ascensor de 3 o 4 plantas es una de las consultas más frecuentes que reciben las empresas...',
    imageUrl: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=300&q=80',
  },
  {
    id: 3,
    title: 'CARACTERÍSTICAS DE LOS MONTACARGAS PARA PERSONAS',
    publishDate: '2024-04-26',
    summary: 'Conoce las principales características y especificaciones técnicas de los montacargas diseñados para personas...',
    imageUrl: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=300&q=80',
  },
];

const ITEMS_PER_PAGE = 3;

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    const months = [
      'ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO',
      'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'
    ];
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  } catch {
    return dateStr;
  }
};

const HomeBlogSection = ({ posts = [] }) => {
  const allPosts = posts && posts.length > 0 ? posts : DEFAULT_BLOG_POSTS;
  const totalPages = Math.ceil(allPosts.length / ITEMS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    setCurrentPage(0);
  }, [allPosts.length]);

  const handleNextPage = () => {
    if (totalPages > 1) {
      setCurrentPage((prev) => (prev + 1) % totalPages);
    }
  };

  const displayPosts = allPosts.slice(
    currentPage * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE + ITEMS_PER_PAGE
  );

  return (
    <Box
      sx={{
        borderRadius: '24px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
        bgcolor: '#ffffff',
        p: { xs: 2, sm: 2.5 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minWidth: 0,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, minWidth: 0 }}>
        <Typography
          variant="h6"
          component="a"
          href="https://www.aszende.com/blog/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            fontWeight: 800,
            color: '#1f7bb6',
            fontSize: '1.05rem',
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          BLOG
        </Typography>
        <Typography
          component="a"
          href="https://www.aszende.com/blog/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            fontSize: '0.8rem',
            color: '#257eb9',
            fontWeight: 700,
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          Ver todo →
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: displayPosts.length < 3 ? 'flex-start' : 'space-between',
          gap: 1.5,
          flex: 1,
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        {displayPosts.map((post) => {
          const targetUrl = post.linkUrl || post.link || 'https://www.aszende.com/blog/';

          return (
            <Box
              key={post.id || post.title}
              onClick={() => window.open(targetUrl, '_blank', 'noopener,noreferrer')}
              sx={{
                display: 'flex',
                gap: 1.5,
                alignItems: 'center',
                transition: 'transform 0.2s',
                cursor: 'pointer',
                minWidth: 0,
                width: '100%',
                '&:hover': { transform: 'translateX(3px)' },
              }}
            >
              {/* Thumbnail */}
              <Box
                component="img"
                src={
                  post.imageUrl ||
                  'https://images.unsplash.com/photo-1541888946425-d0fbb18615f3?w=300&q=80'
                }
                alt={post.title}
                loading="lazy"
                sx={{
                  width: { xs: 64, sm: 72 },
                  height: { xs: 50, sm: 54 },
                  borderRadius: '10px',
                  objectFit: 'cover',
                  flexShrink: 0,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
              />

              {/* Info */}
              <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                <Typography
                  sx={{
                    color: '#64748b',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    letterSpacing: 0.3,
                    mb: 0.2,
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {formatDate(post.publishDate)}
                </Typography>
                <Typography
                  sx={{
                    color: '#1f7bb6',
                    fontSize: '0.84rem',
                    fontWeight: 700,
                    lineHeight: 1.25,
                    mb: 0.3,
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {post.title}
                </Typography>
                <Typography
                  sx={{
                    color: '#475569',
                    fontSize: '0.76rem',
                    lineHeight: 1.35,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {post.summary || post.content}
                  <Typography
                    component="span"
                    sx={{
                      color: '#0f172a',
                      fontWeight: 700,
                      fontSize: '0.76rem',
                      ml: 0.5,
                      whiteSpace: 'nowrap',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Leer más
                  </Typography>
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Down Chevron / Paginador */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          pt: 1,
          mt: 'auto',
        }}
      >
        {totalPages > 1 ? (
          <IconButton
            size="small"
            onClick={handleNextPage}
            title="Ver más artículos"
            sx={{
              color: '#257eb9',
              p: 0.5,
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(2px)',
                bgcolor: 'rgba(37, 126, 185, 0.08)',
              },
            }}
          >
            <KeyboardArrowDown sx={{ fontSize: '1.8rem' }} />
          </IconButton>
        ) : (
          <Box
            component="a"
            href="https://www.aszende.com/blog/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: '#257eb9',
              textDecoration: 'none',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'translateY(2px)' },
            }}
          >
            <KeyboardArrowDown sx={{ fontSize: '1.8rem' }} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

HomeBlogSection.propTypes = {
  posts: PropTypes.array,
};

export default HomeBlogSection;


